import { useCallback, useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { ScanField } from "@/components/ScanField";
import { scanAccept, scanRefuse } from "@/lib/scanFeedback";
import { toast } from "sonner";
import { GitMerge, Undo2, Loader2, AlertTriangle, Check, Copy, Lock } from "lucide-react";
import { Link } from "react-router-dom";

// MERGE TWO RECORDS OF THE SAME TRUCK. His rulings drive every shape here:
//   · NOTHING PRE-SELECTED. "A decision this expensive to get wrong shouldn't have a default
//     somebody clicks past" — the survivor's cost is the one that lives.
//   · A LOUD banner when the two costs disagree, because that IS the decision.
//   · WHAT CARRIES ACROSS, field by field, in his words.
//   · For a one-character VIN difference he picks WHICH VIN IS REAL, separately from which record
//     survives — the survivor might be the one with the typo.
//   · INVISIBLE without `merge_trucks`. No greyed button, no permission message. Absent.
const LEVEL = {
  critical: "border-[#B01E28] bg-red-50 text-[#B01E28]",
  high: "border-amber-400 bg-amber-50 text-amber-900",
  normal: "border-slate-300 bg-white text-slate-700",
};

const money = (n) => (n === null || n === undefined ? "—" : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

function Side({ s, chosen, onChoose, testid }) {
  return (
    <button type="button" data-testid={testid} onClick={onChoose}
      className={`text-left rounded-xl border-2 p-3 transition-all ${
        chosen ? "border-[#E0781A] bg-orange-50" : "border-slate-200 bg-white hover:border-slate-400"}`}>
      <p className="font-mono font-bold text-slate-900 flex items-center gap-1.5">
        {chosen && <Check className="w-4 h-4 text-[#E0781A]" />}{s.stock_number}
      </p>
      <p className="text-xs text-slate-500">{s.ymm || "—"}</p>
      <dl className="mt-2 space-y-0.5 text-[11px] text-slate-600">
        <div className="flex justify-between"><dt>VIN</dt><dd className="font-mono">{s.vin_tail || "none"}</dd></div>
        <div className="flex justify-between"><dt>Cost</dt><dd data-testid={`${testid}-cost`} className="font-bold tabular-nums">{money(s.cost)}</dd></div>
        <div className="flex justify-between"><dt>Parts</dt><dd className="tabular-nums">{s.parts}</dd></div>
        <div className="flex justify-between"><dt>Lots</dt><dd className="tabular-nums">{s.lots}</dd></div>
        <div className="flex justify-between"><dt>Photos</dt><dd className="tabular-nums">{s.photos}</dd></div>
        <div className="flex justify-between"><dt>Build sheet</dt><dd>{s.build_sheet ? "yes" : "no"}</dd></div>
        <div className="flex justify-between"><dt>Specs</dt><dd className="tabular-nums">{s.specs_recorded}</dd></div>
        <div className="flex justify-between"><dt>Location</dt><dd>{s.location || "none"}</dd></div>
        <div className="flex justify-between"><dt>Stage</dt><dd>{s.stage || "—"}</dd></div>
        <div className="flex justify-between"><dt>Created</dt><dd>{String(s.created_at || "").slice(0, 10)}</dd></div>
      </dl>
      <p className={`mt-2 text-[11px] font-bold ${chosen ? "text-[#C76714]" : "text-slate-400"}`}>
        {chosen ? "KEEPING THIS ONE" : "Keep this one"}
      </p>
    </button>
  );
}

export function TruckMergeAction({ aId, bId, bScan, trigger, onDone, autoOpen = false,
                                   testid = "truck-merge" }) {
  const { has } = useAuth();
  const [open, setOpen] = useState(autoOpen);
  const [cmp, setCmp] = useState(null);
  const [err, setErr] = useState("");
  const [survivor, setSurvivor] = useState(null);     // NOTHING pre-selected, on purpose
  const [keepVin, setKeepVin] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setErr(""); setCmp(null); setSurvivor(null); setKeepVin(null); setPreview(null);
    try {
      const { data } = await api.get("/truck-merge/compare",
        { params: { a_id: aId, ...(bId ? { b_id: bId } : { b_scan: bScan }) } });
      setCmp(data);
      if (!data.vin?.must_choose) setKeepVin(data.vin?.options?.[0] || null);
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail)); }
  }, [aId, bId, bScan]);

  useEffect(() => { if (open) load(); }, [open, load]);

  if (!has("merge_trucks")) return null;

  const loser = survivor && cmp ? (survivor === cmp.a.id ? cmp.b : cmp.a) : null;
  const survivorSide = survivor && cmp ? (survivor === cmp.a.id ? cmp.a : cmp.b) : null;
  const plan = !cmp || !survivor ? [] : (survivor === cmp.a.id ? cmp.carry_if_a_survives : cmp.carry_if_b_survives);

  const ask = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/truck-merge", {
        survivor_id: survivor, loser_id: loser.id, keep_vin: keepVin, confirm: false });
      setPreview(data);
    } catch (e) { scanRefuse(formatApiErrorDetail(e.response?.data?.detail)); toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const commit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/truck-merge", {
        survivor_id: survivor, loser_id: loser.id, keep_vin: keepVin, confirm: true });
      scanAccept(data.message);
      toast.success(data.message, {
        duration: 15000,
        action: {
          label: "Undo",
          onClick: async () => {
            try {
              const r = await api.post(`/truck-merge/${data.merge_id}/undo?confirm=true`);
              toast.success(r.data.message); onDone?.();
            } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
          },
        },
      });
      setOpen(false); onDone?.();
    } catch (e) { scanRefuse(formatApiErrorDetail(e.response?.data?.detail)); toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <>
      {trigger
        ? <span onClick={() => setOpen(true)}>{trigger}</span>
        : <Button data-testid={`${testid}-open`} variant="outline" onClick={() => setOpen(true)}
            className="rounded-lg border-[#B01E28] text-[#B01E28] hover:bg-red-50 h-9">
            <GitMerge className="w-4 h-4 mr-1.5" />Merge these two
          </Button>}

      <Dialog open={open} onOpenChange={(v) => { if (!v) { setOpen(false); setPreview(null); } }}>
        <DialogContent className="rounded-xl max-w-2xl max-h-kb overflow-y-auto">
          <DialogHeader>
            <DialogTitle data-testid={`${testid}-title`}>Same truck, entered twice?</DialogTitle>
            <DialogDescription>
              One record survives and the other is archived — never deleted, and its old tag will
              still scan through to the one you keep. Pick the record to KEEP: its acquisition cost
              is the one that lives on.
            </DialogDescription>
          </DialogHeader>

          {err && <p data-testid={`${testid}-error`}
            className="rounded-lg border-2 border-[#B01E28] bg-red-50 px-3 py-2 text-sm font-semibold text-[#B01E28]">{err}</p>}

          {cmp && (
            <div className="space-y-3">
              {cmp.cost?.disagree && (
                <p data-testid={`${testid}-cost-warning`}
                  className="rounded-lg border-2 border-[#B01E28] bg-red-50 px-3 py-2 text-sm font-bold text-[#B01E28] flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />{cmp.cost.text}
                </p>
              )}
              <p data-testid={`${testid}-vin-note`} className="text-xs text-slate-600">{cmp.vin?.text}</p>

              <div className="grid grid-cols-2 gap-3">
                <Side s={cmp.a} chosen={survivor === cmp.a.id} testid={`${testid}-keep-a`}
                  onChoose={() => { setSurvivor(cmp.a.id); setPreview(null); }} />
                <Side s={cmp.b} chosen={survivor === cmp.b.id} testid={`${testid}-keep-b`}
                  onChoose={() => { setSurvivor(cmp.b.id); setPreview(null); }} />
              </div>

              {cmp.vin?.must_choose && (
                <div data-testid={`${testid}-vin-choice`} className="rounded-lg border-2 border-amber-400 bg-amber-50 p-2.5">
                  <p className="text-xs font-bold text-amber-900 mb-1.5">Which VIN is the real one?</p>
                  <div className="flex flex-wrap gap-2">
                    {cmp.vin.options.map((v, i) => (
                      <button key={v} type="button" data-testid={`${testid}-vin-${i}`}
                        onClick={() => { setKeepVin(v); setPreview(null); }}
                        className={`rounded-lg border-2 px-3 h-10 font-mono text-sm font-bold transition-all ${
                          keepVin === v ? "border-[#E0781A] bg-white text-[#C76714]" : "border-slate-300 bg-white text-slate-600"}`}>
                        {v}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {survivor && !!plan.length && (
                <div data-testid={`${testid}-carry`} className="rounded-lg border border-slate-200 bg-slate-50 p-2.5 space-y-1">
                  <p className="text-xs font-bold text-slate-700">
                    What happens to {loser.stock_number}'s information
                  </p>
                  {plan.map((c, i) => (
                    <p key={i} data-testid={`${testid}-carry-${c.field}`}
                      className={`text-[11px] ${c.carries ? "text-emerald-700 font-semibold" : "text-slate-500"}`}>
                      {c.carries ? "→ " : "· "}{c.text}
                    </p>
                  ))}
                </div>
              )}

              {preview && (
                <div data-testid={`${testid}-confirm`} className="rounded-lg border-2 border-slate-300 bg-slate-50 p-3 space-y-2">
                  <p className="text-sm font-semibold text-slate-800">{preview.prompt}</p>
                  {(preview.warnings || []).map((w, i) => (
                    <p key={i} data-testid={`${testid}-warning-${i}`}
                      className={`text-xs font-semibold rounded-md border px-2 py-1.5 flex items-start gap-1.5 ${LEVEL[w.level] || LEVEL.normal}`}>
                      <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />{w.text}
                    </p>
                  ))}
                  <p className="text-[11px] text-slate-500">{preview.archived_note}</p>
                  <p className="text-[11px] text-slate-500">{preview.reversible}</p>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            {preview ? (
              <Button data-testid={`${testid}-commit`} onClick={commit} disabled={busy}
                className="rounded-lg bg-[#B01E28] hover:bg-[#8F1820] text-white h-11">
                {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null}
                Yes — fold {loser?.stock_number} into {survivorSide?.stock_number}
              </Button>
            ) : (
              <Button data-testid={`${testid}-next`} onClick={ask}
                disabled={busy || !cmp || !survivor || (cmp?.vin?.must_choose && !keepVin)}
                className="rounded-lg bg-[#E0781A] hover:bg-[#C76714] text-white h-11 disabled:opacity-50">
                {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null}
                {!survivor ? "Pick the record to keep" : cmp?.vin?.must_choose && !keepVin ? "Say which VIN is real" : "What happens if I do?"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// "THIS IS A DUPLICATE OF…" — how a VIN-LESS double entry gets found at all. Nothing can detect
// those (year/make/model is noise: 42 active 2018 Cascadias), so the person looking at the truck
// starts it. Scan or type the real one and it opens the same side-by-side.
export function DuplicateOfAction({ donor, onDone, testid = "duplicate-of" }) {
  const { has } = useAuth();
  const [open, setOpen] = useState(false);
  const [scan, setScan] = useState("");
  if (!has("merge_trucks") || !donor?.id || donor?.archived) return null;
  return (
    <>
      <Button data-testid={`${testid}-open`} variant="outline" onClick={() => { setScan(""); setOpen(true); }}
        className="rounded-lg border-slate-300 text-slate-700 hover:border-[#B01E28] hover:text-[#B01E28] h-9">
        <Copy className="w-4 h-4 mr-1.5" />This is a duplicate of…
      </Button>
      {/* The scan step HANDS OFF to the side-by-side rather than nesting inside it — one dialog
          on screen at a time, so the decision has the whole screen. */}
      <Dialog open={open && !scan} onOpenChange={(v) => setOpen(v)}>
        <DialogContent className="rounded-xl max-w-md">
          <DialogHeader>
            <DialogTitle data-testid={`${testid}-title`}>Which record is the same truck?</DialogTitle>
            <DialogDescription>
              Scan the other record's label or type its stock number. Nothing happens yet — you'll
              see both side by side and choose which one to keep.
            </DialogDescription>
          </DialogHeader>
          <ScanField testid={`${testid}-scan`} onScan={(v) => { setScan(v); setOpen(false); }}
            submitLabel="Compare them"
            placeholder="Scan the other truck, or type its stock number…" uppercase autoFocus={false} />
        </DialogContent>
      </Dialog>
      {scan && (
        <TruckMergeAction aId={donor.id} bScan={scan} autoOpen testid={`${testid}-merge`}
          onDone={() => { setScan(""); onDone?.(); }}
          trigger={<span className="hidden" />} />
      )}
    </>
  );
}

// THE TRUCK'S OWN MERGE TRAIL — not gated on `merge_trucks`. The ACTION is Admin-only, but what
// happened to a record is part of the record: "someone looking at a truck should see 'merged from
// DNR-00061 on this date by this person' on the truck's own history, not only in /health. Same
// principle as photo moves logged on both ends." No cost figures — only that the cost went with
// the surviving record.
export function MergeHistory({ rows, testid = "merge-history" }) {
  if (!rows?.length) return null;
  return (
    <div data-testid={testid} className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
      <p className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider text-slate-400">
        <GitMerge className="w-3 h-3" />Duplicate records
      </p>
      <ul className="mt-1.5 space-y-1.5">
        {rows.map((r) => (
          <li key={r.id} data-testid={`${testid}-row`} className="text-sm text-slate-700">
            {r.line}{" "}
            {r.other_id && (
              <Link data-testid={`${testid}-link-${r.other_stock}`} to={`/donors/${r.other_id}`}
                className="font-mono text-xs font-bold text-[#C76714] underline hover:no-underline">
                {r.other_stock}
              </Link>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

// THE MERGE IS ONE PRESS BACK, ON THE SURVIVING TRUCK'S OWN PAGE — and the offer disappears with
// a reason once money has touched one of the parts it moved. "An undo that restores the parts but
// not the cost has quietly changed my books", so this restores the whole snapshotted record.
export function MergeUndoLine({ donorId, onDone, testid = "merge-undo" }) {
  const { has } = useAuth();
  const [m, setM] = useState(null);
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!donorId) return;
    try { setM((await api.get(`/truck-merge/for-donor/${donorId}`)).data.merge); } catch { /* never break the page */ }
  }, [donorId]);
  useEffect(() => { if (has("merge_trucks")) load(); }, [load, has]);

  if (!has("merge_trucks") || !m) return null;

  const ask = async () => {
    setBusy(true);
    try { setPreview((await api.post(`/truck-merge/${m.id}/undo`)).data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const commit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/truck-merge/${m.id}/undo?confirm=true`);
      scanAccept(data.message); toast.success(data.message, { duration: 12000 });
      setOpen(false); setPreview(null); await load(); onDone?.();
    } catch (e) { scanRefuse(formatApiErrorDetail(e.response?.data?.detail)); toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid={testid} className="rounded-xl border-2 border-sky-200 bg-sky-50 px-4 py-3">
      <p className="text-sm text-slate-700">
        <b className="font-mono font-bold">{m.loser_stock}</b> was folded into this record
        {m.at ? ` on ${String(m.at).slice(0, 10)}` : ""}{m.by ? ` by ${m.by}` : ""} — {m.parts_moved} part(s) moved across
        {m.discarded_vin ? ", and this truck's VIN was corrected" : ""}.
      </p>
      {m.reversible ? (
        <button type="button" data-testid={`${testid}-open`} onClick={() => { setPreview(null); setOpen(true); }}
          className="mt-1 inline-flex items-center gap-1 text-xs font-bold text-[#B01E28] underline">
          <Undo2 className="w-3.5 h-3.5" />Wrong two records? Split them back apart
        </button>
      ) : (
        <p data-testid={`${testid}-locked`} className="mt-1 text-xs text-slate-500 inline-flex items-center gap-1">
          <Lock className="w-3 h-3" />Can't be split apart now — {m.blocked_because}
        </p>
      )}

      <Dialog open={open} onOpenChange={(v) => { if (!v) { setOpen(false); setPreview(null); } }}>
        <DialogContent className="rounded-xl max-w-lg">
          <DialogHeader>
            <DialogTitle data-testid={`${testid}-title`}>Split these two records apart again?</DialogTitle>
            <DialogDescription>
              {m.loser_stock} comes back exactly as it was — cost included — and only the parts this
              merge moved go back with it.
            </DialogDescription>
          </DialogHeader>
          {preview && (
            <div data-testid={`${testid}-confirm`} className="rounded-lg border-2 border-slate-300 bg-slate-50 p-3 space-y-2">
              <p className="text-sm font-semibold text-slate-800">{preview.prompt}</p>
              {(preview.warnings || []).map((w, i) => (
                <p key={i} data-testid={`${testid}-warning-${i}`}
                  className={`text-xs font-semibold rounded-md border px-2 py-1.5 ${LEVEL[w.level] || LEVEL.normal}`}>{w.text}</p>
              ))}
              <p className="text-[11px] text-slate-500">{preview.reversible}</p>
            </div>
          )}
          <DialogFooter>
            {preview ? (
              <Button data-testid={`${testid}-commit`} onClick={commit} disabled={busy}
                className="rounded-lg bg-[#B01E28] hover:bg-[#8F1820] text-white h-11">
                {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null}Yes — put it back
              </Button>
            ) : (
              <Button data-testid={`${testid}-next`} onClick={ask} disabled={busy}
                className="rounded-lg bg-[#E0781A] hover:bg-[#C76714] text-white h-11">
                {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null}What happens if I do?
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
